import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useSYS3060_R01Store } from 'src/containers/mes/SYS/SYS3060_R01/Store_SYS3060_R01';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultSYS3060_R01HeaderDto } from './Dto_SYS3060_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS3060_R01-grid-column-state';

const headerNames: Record<string, string> = {
	rowIndex: '',
	minorCd: '권한ID',
	codeNm: '권한명',
};

export interface Props {
	gridRef: React.RefObject<AgGridReact<SysCodeDto> | null>;
}

export const Table_SYS3060_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		SYS3060_R01List,
		selectedSYS3060_R01,
		setSelectedSYS3060_R01,
		initSelectedSYS3060_R01,
		setSelectedMenuAuthorityList,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSYS3060_R01Store();

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'minorCd',
		gridData: SYS3060_R01List,
		defaultGridData: defaultSYS3060_R01HeaderDto,
		selectedRow: selectedSYS3060_R01
			? SYS3060_R01List.find((item) => item.minorCd === selectedSYS3060_R01)
			: undefined,
	});

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'minorCd')(params), []);

	const baseColumnDefs: ColDef<SysCodeDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '권한ID', colId: 'minorCd', field: 'minorCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'minorCd'),
		},
		{
			headerName: '권한명', colId: 'codeNm', field: 'codeNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'codeNm'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SysCodeDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SysCodeDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SysCodeDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS3060_R01 === rowData.minorCd;
		setSelectedSYS3060_R01(isSelected ? undefined : rowData.minorCd);
		setSelectedMenuAuthorityList([]);
		setDetailScrollTop(null);
	}, [selectedSYS3060_R01, setSelectedSYS3060_R01, setSelectedMenuAuthorityList, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		minorCd: `${SYS3060_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [SYS3060_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [SYS3060_R01List, selectedSYS3060_R01]);

	return (
		<GridContextWrapper<SysCodeDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS3060_R01}
			defaultRowData={defaultSYS3060_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SysCodeDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SysCodeDto>({
					idKey: 'minorCd',
					selectedList: selectedSYS3060_R01 ? [selectedSYS3060_R01] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
